import {
    get,
    getDatabase,
    onValue,
    ref,
    remove,
    set,
    update,
} from 'firebase/database'
import { getDistance } from 'geolib'
import {
    SchoolInformation,
    SchoolInvoice,
    SchoolInvoiceRequest,
    SchoolLocations,
    SchoolPolicy,
    SchoolType,
    SchoolUser,
    SchoolUserAccountState,
    SchoolUserNotifications,
} from './types-school'
import { Res } from './types'
import { v4 as uuidv4 } from 'uuid'
import { UserType } from '../redux/types'
import { Unsubscribe } from 'firebase/auth'
import { isObjectEmpty } from './general-apis'
import { formatContactNumber } from '../pages/Dashboard/RegistrationModals/components/helper'
import {
    JoinSchoolOnboardingSchema,
    NewSchoolOnboardingSchema,
} from '../pages/OnBoardingPage/School/schema'

import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { User } from '../schemas/user'
import { geohashForLocation } from 'geofire-common'
import { logger } from '../helpers/logger'
dayjs.extend(utc)
dayjs.extend(timezone)

export const getSchoolDistanceInMeter = (
    schoolCoordinates: any,
    currentSchoolLat: number,
    currentSchoolLong: number
) => {
    let currentSchoolCoordinates = {
        latitude: currentSchoolLat,
        longitude: currentSchoolLong,
    }
    let refactorSchoolcoordinates = {
        latitude: schoolCoordinates[0],
        longitude: schoolCoordinates[1],
    }
    let distanceInMeter = getDistance(
        currentSchoolCoordinates,
        refactorSchoolcoordinates,
        10
    )
    return Math.round(distanceInMeter)
}

export const getNearbySchools = async (
    homeCountry: string,
    homeState: string,
    lat: number,
    lng: number
): Promise<
    Res<{ school: SchoolInformation; distance: number; id: string }[]>
> => {
    try {
        let schoolsList: { id: string; distance: number }[] = []
        const SCHOOL_REGISTRATION_NEARBY_RADIUS = 200
        const nodeRef = ref(
            getDatabase(),
            `countries/${homeCountry}/${homeState}/locations/schools`
        )
        const schoolLocationSnapshot = await get(nodeRef)
        if (schoolLocationSnapshot.exists()) {
            const schoolsLocation =
                schoolLocationSnapshot.val() as SchoolLocations
            Object.entries(schoolsLocation).forEach(([key, value]) => {
                let schoolDistance = getSchoolDistanceInMeter(value.l, lat, lng)
                if (schoolDistance <= SCHOOL_REGISTRATION_NEARBY_RADIUS) {
                    schoolsList.push({ id: key, distance: schoolDistance })
                }
            })
        }
        let nearbySchools: {
            school: SchoolInformation
            distance: number
            id: string
        }[] = []
        // Fetch nearby school information
        for (let i of schoolsList) {
            let nodeRef = ref(getDatabase(), `schoolInformation/${i.id}`)
            let snap = await get(nodeRef)
            if (snap.exists()) {
                nearbySchools.push({
                    school: snap.val() as SchoolInformation,
                    distance: i.distance,
                    id: i.id,
                })
            }
        }
        return { code: 200, data: nearbySchools }
    } catch (error) {
        return { code: 500, error: error }
    }
}

export const setSchoolLocation = async (
    homeCountry: string,
    homeState: string,
    schoolId: string,
    loc: { lat: number; lng: number }
) => {
    try {
        const nodeRef = ref(
            getDatabase(),
            `countries/${homeCountry}/${homeState}/locations/schools/${schoolId}`
        )

        await set(nodeRef, {
            g: geohashForLocation([loc.lat, loc.lng], 10),
            l: [loc.lat, loc.lng],
        })
    } catch (error: any) {
        logger.error(`${JSON.stringify(error)}`)
    }
}

export const submitSchoolRegistration = async (
    formData: NewSchoolOnboardingSchema,
    user: User,
    userId: string
): Promise<
    | { code: 200; data: string; school: SchoolInformation }
    | { code: 500; error: any }
> => {
    try {
        let schoolId = uuidv4()

        // Update school information node
        const nodeRef = ref(getDatabase(), `schoolInformation/${schoolId}`)

        const { schoolName, country, locationState, suburb, lat, long } =
            formData.school.data

        let school: SchoolInformation = {
            account: {
                createdBy: userId,
                dateRegistered: dayjs().format('DD-MM-YYYY'),
                registeredBy: userId,
                state: 'pendingApproval',
            },
            businessManager: {
                contact: formData.bm.contact,
                email: formData.bm.email,
                name: formData.bm.name,
                invoiceEmail: formData.bm.invoiceEmail,
                state: 'incomplete',
            },
            details: {
                contactNumber:
                    formData.schoolInformation.contact.type === 'Use my number'
                        ? formData.basicDetails.contact
                        : formData.schoolInformation.contact.value,
                educationType: formData.schoolInformation.educationType,
                privateOrPublic: formData.schoolInformation.publicOrPrivate,
                religion: formData.schoolInformation.religion,
                schoolName,
                schoolType: formData.schoolInformation.type,
                updatedBy: userId,
                contactType: formData.schoolInformation.contact.type,
            },
            location: {
                address: formData.school.data.address,
                country,
                locationState,
                postCode: formData.school.data.postCode,
                suburb,
                lat,
                long,
            },
            primaryTimes: formData.primaryBellTimes
                ? (Object.entries(formData.primaryBellTimes).reduce(
                      (acc, curr) => ({
                          ...acc,
                          [curr[0]]: dayjs
                              .unix(curr[1])
                              .tz('Australia/Melbourne')
                              .format('hh : mm A'),
                      }),
                      {}
                  ) as any)
                : null,
            secondaryTimes: formData.secondaryBellTimes
                ? (Object.entries(formData.secondaryBellTimes).reduce(
                      (acc, curr) => ({
                          ...acc,
                          [curr[0]]: dayjs
                              .unix(curr[1])
                              .tz('Australia/Melbourne')
                              .format('hh : mm A'),
                      }),
                      {}
                  ) as any)
                : null,
        }
        // Update times
        school.times =
            formData.schoolInformation.type !== SchoolType.PRIMARY_SECONDARY
                ? ({
                      ...(school.primaryTimes !== undefined
                          ? { ...school.primaryTimes }
                          : { ...school.secondaryTimes }),
                  } as any)
                : null
        await update(nodeRef, school)

        // Update school location
        await setSchoolLocation(country, locationState, schoolId, {
            lat: lat,
            lng: long,
        })

        // Get Terms Version code
        let version = 'No Terms For User Location'
        const termsNode = ref(
            getDatabase(),
            `countries/${country}/${locationState}/termsAndConditions/schools/documentID`
        )
        const snap = await get(termsNode)
        if (snap.exists()) {
            const temp = snap.val()
            if (temp) {
                version = temp
            }
        }

        // Update school users node
        const schoolUserRef = ref(
            getDatabase(),
            `schoolUsers/${schoolId}/${userId}`
        )
        let schoolUserComplete: SchoolUser = {
            userDetails: {
                contact: formData.basicDetails.contact,
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
                profilePictureURL: user.profilePictureURL || null,
                type: user.profilePictureURL ? 'image' : null,
            },
            school: {
                schoolUid: schoolId,
                schoolCountry: country,
                schoolState: locationState,
                positionTitle: formData.basicDetails.position,
                authority: 'admin',
                accountState: 'pendingSparkApproval',
                dateRegistered: dayjs().format('DD-MM-YYYY'),
            },
            termsAndConditions: {
                dateAccepted: dayjs().format('DD-MM-YYYY'),
                documentID: version,
                timeAccepted: Date.now() / 1000,
            },
            deviceSettings: {
                deviceType: 'web',
            },
            notificationSettings: {
                isEnabled: true,
            },
        }
        await update(schoolUserRef, schoolUserComplete)

        // Update school admins node
        const schoolAdminsNode = ref(
            getDatabase(),
            `schoolAdmins/${schoolId}/${userId}`
        )
        await update(schoolAdminsNode, {
            name: user,
        })

        // Update the user node
        const userNode = ref(getDatabase(), `users/${userId}`)
        await update(userNode, {
            accountType: 'school',
            homeCountry: country,
            homeState: locationState,
            contact: formData.basicDetails.contact,
            schoolUid: schoolId,
            [`schools/${schoolId}`]: {
                name: schoolName,
                suburb: suburb,
            },
        } as Partial<User>)

        return { code: 200, data: schoolId, school }
    } catch (error) {
        return { code: 500, error: error }
    }
}

export const submitJoinSparkSchool = async (
    formData: JoinSchoolOnboardingSchema,
    user: User,
    userId: string
): Promise<
    | { code: 200; data: string; school: SchoolInformation }
    | { code: 500; error: any }
> => {
    try {
        const { country, locationState } = formData.school.data.location

        // Get Terms Version code
        let version = 'No Terms For User Location'
        const termsNode = ref(
            getDatabase(),
            `countries/${country}/${locationState}/termsAndConditions/schools/documentID`
        )
        const snap = await get(termsNode)
        if (snap.exists()) {
            const temp = snap.val()
            if (temp) {
                version = temp
            }
        }

        // Update school users node
        const schoolUserRef = ref(
            getDatabase(),
            `schoolUsers/${formData.school.id}/${userId}`
        )
        let schoolUserComplete: SchoolUser = {
            userDetails: {
                contact: formData.basicDetails.contact,
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
                profilePictureURL: user.profilePictureURL || null,
                type: user.profilePictureURL ? 'image' : null,
            },
            school: {
                schoolUid: formData.school.id,
                schoolCountry: country,
                schoolState: locationState,
                positionTitle: formData.basicDetails.position,
                authority: 'bookingAssistant',
                accountState: 'pendingSchoolAdminApproval',
                dateRegistered: dayjs().format('DD-MM-YYYY'),
            },
            termsAndConditions: {
                dateAccepted: dayjs().format('DD-MM-YYYY'),
                documentID: version,
                timeAccepted: Date.now() / 1000,
            },
            deviceSettings: {
                deviceType: 'web',
            },
            notificationSettings: {
                isEnabled: true,
            },
        }
        await update(schoolUserRef, schoolUserComplete)

        // Update the user node
        const userNode = ref(getDatabase(), `users/${userId}`)
        await update(userNode, {
            accountType: 'school',
            homeCountry: country,
            homeState: locationState,
            contact: formData.basicDetails.contact,
            schoolUid: formData.school.id,
            [`schools/${formData.school.id}`]: {
                name: formData.school.data.details.schoolName,
                suburb: formData.school.data.location.suburb,
            },
        })

        return {
            code: 200,
            data: formData.school.id,
            school: formData.school.data,
        }
    } catch (error) {
        return { code: 500, error: error }
    }
}

export const updateSchoolUserDetails = async (
    userId: string,
    data: {
        firstName?: string
        lastName?: string
        firstNameActual?: string | null
        contact?: string
        email?: string
    }
) => {
    try {
        const { firstName, lastName, contact, email } = data

        // Update users node
        const userNode = ref(getDatabase(), `users/${userId}`)
        const snap = await get(userNode)
        if (!snap.exists()) {
            throw new Error('User does not exist')
        }
        const userData = snap.val() as UserType

        // Update school users node from list of schools
        if (!userData.schools) {
            return { code: 200 }
        }
        for (let schoolId of Object.keys(userData.schools)) {
            const schoolUserNode = ref(
                getDatabase(),
                `schoolUsers/${schoolId}/${userId}/userDetails`
            )
            await update(schoolUserNode, {
                ...(firstName !== undefined ? { firstName: firstName } : null),
                ...(lastName !== undefined ? { lastName: lastName } : null),
                ...(contact !== undefined ? { contact: contact } : null),
                ...(email !== undefined ? { email: email } : null),
            })
        }
        return { code: 200 }
    } catch (error) {
        return { code: 500, error: error }
    }
}

export const updateSchoolUserNotifications = async (
    schoolId: string,
    userId: string,
    notifications: SchoolUserNotifications
) => {
    try {
        const schoolUserNode = ref(
            getDatabase(),
            `schoolUsers/${schoolId}/${userId}`
        )
        await update(schoolUserNode, {
            notificationSettings: notifications,
        })
        return { code: 200 }
    } catch (error) {
        return { code: 500, error: error }
    }
}

export const listenSchoolUsers = (
    schoolId: string,
    setRows: (users: SchoolUser[]) => void,
    setLoading: (loading: boolean) => void
): Res<Unsubscribe> => {
    try {
        const schoolUserNode = ref(getDatabase(), `schoolUsers/${schoolId}`)
        const unsub = onValue(schoolUserNode, (snapshot) => {
            if (snapshot.exists()) {
                let users: SchoolUser[] = []
                for (let [id, user] of Object.entries(snapshot.val())) {
                    let newUser: SchoolUser = user as SchoolUser
                    newUser.id = id
                    if (newUser.school?.accountState !== 'removed') {
                        users.push(newUser as SchoolUser)
                    }
                }
                setRows(users)
            }
        })
        setLoading(false)
        return { code: 200, data: unsub }
    } catch (error) {
        setLoading(false)
        return { code: 500, error: error }
    }
}

export const listenSchoolInvoices = (
    schoolId: string,
    setRows: (invoices: SchoolInvoice[]) => void,
    setLoading: (loading: boolean) => void
): Res<Unsubscribe> => {
    try {
        const schoolInvoiceNode = ref(
            getDatabase(),
            `schoolInvoices/${schoolId}`
        )
        const unsub = onValue(schoolInvoiceNode, (snapshot) => {
            if (snapshot.exists()) {
                let invoices: SchoolInvoice[] = []
                for (let [id, invoice] of Object.entries(snapshot.val())) {
                    let newInvoice = invoice as SchoolInvoice
                    newInvoice.invoiceId = id
                    invoices.push(newInvoice)
                }
                setRows(invoices)
            }
        })
        setLoading(false)
        return { code: 200, data: unsub }
    } catch (error) {
        setLoading(false)
        return { code: 500, error: error }
    }
}

export const updateSchoolUserAdmin = async (
    schoolId: string,
    schoolUserId: string,
    authority: string
) => {
    try {
        const schoolUserNode = ref(
            getDatabase(),
            `schoolUsers/${schoolId}/${schoolUserId}/school`
        )
        await update(schoolUserNode, {
            authority: authority,
        })
        return { code: 200 }
    } catch (error) {
        return { code: 500, error: error }
    }
}

export const checkSchoolAdmins = async (
    schoolId: string
): Promise<Res<number>> => {
    try {
        const schoolAdminNode = ref(getDatabase(), `schoolAdmins/${schoolId}`)
        let count = 0
        const snap = await get(schoolAdminNode)
        if (snap.exists()) {
            count = Object.entries(snap.val()).length
        } else {
            count = 0
        }
        return { code: 200, data: count }
    } catch (error) {
        return { code: 500, error: error }
    }
}

export const requestInvoiceCopy = async (
    invoiceId: string,
    request: SchoolInvoiceRequest
) => {
    try {
        const invoiceReqNode = ref(
            getDatabase(),
            `adminNotifications/invoiceRequest/${invoiceId}`
        )
        await update(invoiceReqNode, request)
        return { code: 200 }
    } catch (error) {
        return { code: 500, error: error }
    }
}

export const approveSchoolUser = async (
    schoolId: string,
    schoolUserId: string,
    registeredBy: string
) => {
    try {
        const schoolUserNode = ref(
            getDatabase(),
            `schoolUsers/${schoolId}/${schoolUserId}/school`
        )
        await update(schoolUserNode, {
            accountState: 'approved',
            approvedBy: registeredBy,
            approvedOnDate: dayjs()
                .tz('Australia/Melbourne')
                .format('DD-MM-YYYY'),
        })

        return { code: 200 }
    } catch (error) {
        return { code: 500, error: error }
    }
}

export const removeSchoolUser = async (
    schoolId: string,
    schoolUserId: string,
    removedBy: string
) => {
    try {
        const schoolUserNode = ref(
            getDatabase(),
            `schoolUsers/${schoolId}/${schoolUserId}/school`
        )
        await update(schoolUserNode, {
            accountState: 'removed'.valueOf(),
            removedBy: removedBy,
            removedOnDate: dayjs()
                .tz('Australia/Melbourne')
                .format('DD-MM-YYYY'),
        })

        return { code: 200 }
    } catch (error) {
        return { code: 500, error: error }
    }
}

export const addPublicPolicy = async (
    schoolId: string,
    userId: string,
    policy: SchoolPolicy
) => {
    try {
        const policyNode = ref(
            getDatabase(),
            `schoolInformation/${schoolId}/policies`
        )
        await update(policyNode, {
            publicUrl: policy.publicUrl,
            timeUpdated: new Date().getTime() / 1000,
            updatedBy: userId,
        })
        return { code: 200 }
    } catch (error) {
        return { code: 500, error: error }
    }
}

export const addAdditionalPolicy = async (
    schoolId: string,
    userId: string,
    policy: SchoolPolicy
) => {
    try {
        let policyId = policy.id || uuidv4()
        const policyNode = ref(
            getDatabase(),
            `schoolInformation/${schoolId}/policies/documents/${policyId}`
        )
        let newFlag = policy.id === undefined
        await update(policyNode, {
            ...(newFlag ? { createdByUserUid: userId } : null),
            ...(newFlag ? { timeCreated: new Date().getTime() / 1000 } : null),
            timeUpdated: new Date().getTime() / 1000,
            title: policy.title,
            updatedBy: userId,
            url: policy.url,
        })
        return { code: 200 }
    } catch (error) {
        return { code: 500, error: error }
    }
}

export const removeAdditionalPolicy = async (
    schoolId: string,
    policyId: string
) => {
    try {
        const policyNode = ref(
            getDatabase(),
            `schoolInformation/${schoolId}/policies/documents/${policyId}`
        )
        await remove(policyNode)
        return { code: 200 }
    } catch (error) {
        return { code: 500, error: error }
    }
}

export const updateTimes = async (
    schoolId: string,
    primaryTimes?: SchoolInformation['primaryTimes'],
    secondaryTimes?: SchoolInformation['secondaryTimes']
) => {
    try {
        const timesNode = ref(getDatabase(), `schoolInformation/${schoolId}`)
        await update(timesNode, {
            ...(primaryTimes && !isObjectEmpty(primaryTimes)
                ? {
                      primaryTimes: { ...primaryTimes },
                  }
                : null),
            ...(secondaryTimes && !isObjectEmpty(secondaryTimes)
                ? {
                      secondaryTimes: { ...secondaryTimes },
                  }
                : null),
        })
        return { code: 200 }
    } catch (error) {
        return { code: 500, error: error }
    }
}

export const setSchoolUid = async (schoolId: string, userId: string) => {
    try {
        const userNode = ref(getDatabase(), `users/${userId}`)
        await update(userNode, {
            schoolUidWeb: schoolId,
        })
        return { code: 200 }
    } catch (error) {
        return { code: 500, error: error }
    }
}

export const updateSchoolContact = async (
    schoolId: string,
    contact: string
) => {
    try {
        const schoolNode = ref(
            getDatabase(),
            `schoolInformation/${schoolId}/details`
        )
        await update(schoolNode, {
            contactNumber: formatContactNumber(contact),
        })
    } catch (error) {
        return { code: 500, error: error }
    }
}

export const updateSchoolParking = async (
    schoolId: string,
    parking: SchoolInformation['parking']
) => {
    try {
        const schoolParkingNode = ref(
            getDatabase(),
            `schoolInformation/${schoolId}/parking`
        )
        await update(schoolParkingNode, {
            ...(parking?.address ? { address: parking.address } : null),
            ...(parking?.information
                ? { information: parking.information }
                : null),
            ...(parking?.lat ? { lat: parking.lat } : null),
            ...(parking?.long ? { long: parking.long } : null),
            ...(parking?.type ? { type: parking.type } : null),
        })
        return { code: 200 }
    } catch (error) {
        return { code: 500, error: error }
    }
}

export const deleteSchoolUser = async (schoolId: string, userId: string) => {
    try {
        const schoolUserNode = ref(
            getDatabase(),
            `schoolUsers/${schoolId}/${userId}`
        )
        await update(schoolUserNode, {
            tokens: null,
            token: null,
        })

        const userUpdate = {
            accountState: 'removed',
            removedBy: userId,
            removedOnDate: dayjs().local().format('DD-MM-YYYY'),
        }

        const schoolUserSchoolNode = ref(
            getDatabase(),
            `schoolUsers/${schoolId}/${userId}/school`
        )
        await update(schoolUserSchoolNode, userUpdate)

        const deletedUsersNode = ref(getDatabase(), `deletedUsers/${userId}`)
        await update(deletedUsersNode, userUpdate)

        const schoolAdminsNode = ref(
            getDatabase(),
            `schoolAdmins/${schoolId}/${userId}`
        )
        await remove(schoolAdminsNode)

        return { code: 200 }
    } catch (error) {
        return { code: 500, error: error }
    }
}

export const countUserApprovedSchools = async (
    userId: string,
    schoolIds: string[]
): Promise<Res<number>> => {
    try {
        let count = 0

        for (let schoolId of schoolIds) {
            const node = ref(getDatabase(), `schoolUsers/${schoolId}/${userId}`)
            const snap = await get(node)
            if (snap.exists()) {
                if (
                    (snap.val().school
                        .accountState as SchoolUserAccountState) === 'approved'
                ) {
                    count = count + 1
                }
            }
        }
        logger.debug(`count = ${count}`)

        return { code: 200, data: count }
    } catch (error) {
        return { code: 500, error: error }
    }
}
